/**
 * How the screens follow Repository Intelligence's asynchronous work — a question, an analysis, a registration, a
 * sync (MASTER-SPEC §14.4, §14.10): the first wait is what the server suggested (`pollAfterMs`), each next one half
 * as long again, never more than ten seconds; four times longer while the tab is hidden; and after two minutes the
 * screen stops and says it is still working, with a button to look again.
 */

/** After this long, stop asking and say "still working". */
export const POLL_LIMIT_MS = 120_000;
/** No wait is longer than this while the tab is visible. */
export const POLL_MAX_DELAY_MS = 10_000;
/** The shortest wait, whatever the server suggests. */
export const POLL_MIN_DELAY_MS = 500;
/** While nobody is looking, ask this many times less often. */
export const HIDDEN_FACTOR = 4;
const HIDDEN_MAX_DELAY_MS = 60_000;
const BACKOFF = 1.5;
/** When the server suggests nothing, as for a sync history that does not say. */
const DEFAULT_DELAY_MS = 2_000;

export interface PollInput {
  /** The server's suggestion; null once the work is final. */
  pollAfterMs: number | null | undefined;
  /** Polls already made in this window, from 0. */
  attempt: number;
  /** How long this window has been open. */
  elapsedMs: number;
  /** Whether the tab is hidden. */
  hidden: boolean;
}

/** Milliseconds until the next poll, or false to stop: final, or out of time. */
export function pollDelay({ pollAfterMs, attempt, elapsedMs, hidden }: PollInput): number | false {
  if (pollAfterMs === null || elapsedMs >= POLL_LIMIT_MS) {
    return false;
  }
  const suggested = pollAfterMs ?? DEFAULT_DELAY_MS;
  const base = Math.min(Math.max(suggested, POLL_MIN_DELAY_MS), POLL_MAX_DELAY_MS);
  const backedOff = Math.min(base * BACKOFF ** Math.max(attempt, 0), POLL_MAX_DELAY_MS);
  const delay = hidden ? Math.min(backedOff * HIDDEN_FACTOR, HIDDEN_MAX_DELAY_MS) : backedOff;
  return Math.round(delay);
}
